const NULL_MAPPING = {
  fileId: "",
  filename: "",
  originalFileId: "", // For PDF extraction: stores original PDF file ID
  originalFilename: "", // For PDF extraction: stores original PDF filename
  extractionDetails: null, // For PDF extraction: stores complete extraction metadata
  origin: { column: 0, row: 0 },
  mapping: [],
  appliedExcess: null,
  dateConversionTypes: {},
  substitutions: [],
  rowDeletions: [],
};

const NULL_MULTISHEET_MAPPING = {
  fileId: "",
  filename: "",
  sheets: [], // Array of sheet configurations
  appliedExcess: null,
};

export const clearMapping = () => NULL_MAPPING;
export const clearMultiSheetMapping = () => NULL_MULTISHEET_MAPPING;

// Multi-sheet helpers
export const createSheetConfig = (sheetName, sheetData = {}) => ({
  tableName: sheetName,
  origin: sheetData.origin || { column: 0, row: 0 },
  mapping: sheetData.mapping || [],
  dateConversionTypes: sheetData.dateConversionTypes || {},
  substitutions: sheetData.substitutions || [],
  rowDeletions: sheetData.rowDeletions || [],
  headerRows: sheetData.headerRows || 1,
});

export const initializeMultiSheetMapping = (
  cd,
  { fileId, filename, selectedSheets, sheetAnalysisResults }
) => {
  const sheets = selectedSheets.map((sheetName) => {
    const sheetData = sheetAnalysisResults?.sheets?.find(
      (s) => s.sheet_name === sheetName
    );
    let origin = { row: 0, column: 0 };

    if (sheetData?.table_start) {
      const row = Math.max(0, (sheetData.table_start.row || 1) - 1);
      const column = Math.max(0, (sheetData.table_start.column || 1) - 1);
      origin = { row, column };
    }

    return createSheetConfig(sheetName, { origin });
  });

  return {
    fileId,
    filename,
    sheets,
    appliedExcess: cd?.appliedExcess || null,
  };
};

export const setFilename = (cd, { filename }) => {
  if (cd?.filename === filename) {
    return cd;
  }
  if (!filename) {
    return {
      ...NULL_MAPPING,
    };
  }
  return {
    ...NULL_MAPPING,
    fileId: cd?.fileId || NULL_MAPPING.fileId,
    filename,
  };
};

export const setFileId = (cd, { fileId }) => {
  if (cd.fileId === fileId) {
    return cd;
  }
  return {
    ...NULL_MAPPING,
    fileId,
    filename: cd.filename || NULL_MAPPING.filename,
    originalFileId: cd.originalFileId || NULL_MAPPING.originalFileId,
    originalFilename: cd.originalFilename || NULL_MAPPING.originalFilename,
    extractionDetails: cd.extractionDetails || NULL_MAPPING.extractionDetails,
  };
};

export const setOriginalFileId = (cd, { originalFileId }) => {
  if (cd.originalFileId === originalFileId) {
    return cd;
  }
  return {
    ...cd,
    originalFileId,
  };
};

export const setOriginalFilename = (cd, { originalFilename }) => {
  if (cd.originalFilename === originalFilename) {
    return cd;
  }
  return {
    ...cd,
    originalFilename,
  };
};

export const setExtractionDetails = (cd, { extractionDetails }) => {
  return {
    ...cd,
    extractionDetails,
  };
};

export const setTableName = (cd, { tableName }) => {
  if (cd.tableName === tableName) {
    return cd;
  }
  return {
    ...NULL_MAPPING,
    fileId: cd.fileId,
    filename: cd.filename,
    originalFileId: cd.originalFileId,
    originalFilename: cd.originalFilename,
    extractionDetails: cd.extractionDetails,
    tableName,
  };
};

export const setOrigin = (cd, { origin }) => {
  if (cd.origin?.column === origin.column && cd.origin?.row === origin.row) {
    return cd;
  }
  return {
    ...NULL_MAPPING,
    fileId: cd.fileId,
    tableName: cd.tableName,
    filename: cd.filename,
    originalFileId: cd.originalFileId,
    originalFilename: cd.originalFilename,
    extractionDetails: cd.extractionDetails,
    origin,
  };
};

export const setHeaderRows = (cd, { headerRows }) => {
  if (cd.headerRows === headerRows) {
    return cd;
  }
  return {
    fileId: cd.fileId,
    filename: cd.filename,
    tableName: cd.tableName,
    origin: cd.origin,
    headerRows,
  };
};

export const setAppliedExcess = (cd, { appliedExcess }) => {
  if (JSON.stringify(cd.appliedExcess) === JSON.stringify(appliedExcess)) {
    return cd;
  }
  return {
    ...cd,
    appliedExcess: appliedExcess,
  };
};

// Multi-sheet versions
export const updateSheetConfig = (cd, { sheetName, updateFn }) => {
  if (!cd.sheets) {
    throw new Error("Cannot update sheet config on non-multi-sheet mapping");
  }

  const sheets = cd.sheets.map((sheet) =>
    sheet.tableName === sheetName ? updateFn(sheet) : sheet
  );

  return {
    ...cd,
    sheets,
  };
};

export const updateMultiSheetColumnMapping = (
  cd,
  { sheetName, inputColumn, outputName, conversionType }
) => {
  return updateSheetConfig(cd, {
    sheetName,
    updateFn: (sheet) => {
      const safeMapping = sheet.mapping ?? [];
      const currentIndex = safeMapping.findIndex(
        (mapping) => mapping.outputName === outputName
      );

      if (
        currentIndex >= 0 &&
        !changesMapping(safeMapping[currentIndex], {
          inputColumn,
          outputName,
          conversionType,
        })
      ) {
        return sheet;
      }

      return {
        ...sheet,
        mapping: newMapping(safeMapping, currentIndex, {
          inputColumn,
          outputName,
          conversions: [{ conversionType }],
        }),
      };
    },
  });
};

export const updateMultiSheetConversionMapping = (
  cd,
  { sheetName, outputName, valueKey, mappedKey }
) => {
  return updateSheetConfig(cd, {
    sheetName,
    updateFn: (sheet) => {
      const safeMapping = sheet.mapping ?? [];
      const currentIndex = safeMapping.findIndex(
        (mapping) => mapping.outputName === outputName
      );
      const conversions = safeMapping[currentIndex]?.conversions ?? [];
      const allowedConversionTypes = [
        "CONVERT",
        "IN_DELETE_ROW",
        "NOT_IN_DELETE_ROW",
      ];
      const conversionIndex = conversions.findIndex((conversion) =>
        allowedConversionTypes.includes(conversion.conversionType)
      );

      if (conversionIndex === -1) {
        throw new Error(
          `Cannot update conversion mapping for types other than ${allowedConversionTypes.join(
            ", "
          )}`
        );
      }

      if (conversions[conversionIndex]?.conversions?.[valueKey] === mappedKey) {
        return sheet;
      }

      return {
        ...sheet,
        mapping: newMapping(safeMapping, currentIndex, {
          inputColumn: safeMapping[currentIndex].inputColumn,
          outputName,
          conversions: [
            ...conversions.slice(0, conversionIndex),
            {
              ...conversions[conversionIndex],
              conversions: {
                ...conversions[conversionIndex].conversions,
                [valueKey]: mappedKey,
              },
            },
            ...conversions.slice(conversionIndex + 1),
          ],
        }),
      };
    },
  });
};

export const updateMultiSheetConstantConversion = (
  cd,
  { sheetName, outputName, value }
) => {
  return updateSheetConfig(cd, {
    sheetName,
    updateFn: (sheet) => {
      const safeMapping = sheet.mapping ?? [];
      const currentIndex = safeMapping.findIndex(
        (mapping) => mapping.outputName === outputName
      );

      if (currentIndex === -1) {
        throw new Error(`Cannot find mapping for output ${outputName}`);
      }

      const conversions = safeMapping[currentIndex]?.conversions ?? [];
      const conversionIndex = conversions.findIndex(
        (conversion) => conversion.conversionType === "CONSTANT"
      );

      if (conversionIndex === -1) {
        throw new Error(
          `Cannot update constant conversion for ${outputName}: no CONSTANT conversion found`
        );
      }

      if (conversions[conversionIndex].value === value) {
        return sheet;
      }

      return {
        ...sheet,
        mapping: newMapping(safeMapping, currentIndex, {
          inputColumn: safeMapping[currentIndex].inputColumn,
          outputName,
          conversions: [
            ...conversions.slice(0, conversionIndex),
            {
              ...conversions[conversionIndex],
              value,
            },
            ...conversions.slice(conversionIndex + 1),
          ],
        }),
      };
    },
  });
};

export const updateMultiSheetDateConversionType = (
  cd,
  { sheetName, column, type }
) => {
  return updateSheetConfig(cd, {
    sheetName,
    updateFn: (sheet) => {
      if ((sheet.dateConversionTypes ?? {})[column] === type) {
        return sheet;
      }

      return {
        ...sheet,
        dateConversionTypes: {
          ...sheet.dateConversionTypes,
          [column]: type,
        },
      };
    },
  });
};

export const setMultiSheetSubstitutions = (
  cd,
  { sheetName, substitutions }
) => {
  return updateSheetConfig(cd, {
    sheetName,
    updateFn: (sheet) => {
      if (
        JSON.stringify(sheet.substitutions) === JSON.stringify(substitutions)
      ) {
        return sheet;
      }

      return {
        ...sheet,
        substitutions: substitutions ?? [],
      };
    },
  });
};

export const setMultiSheetRowDeletions = (cd, { sheetName, rowDeletions }) => {
  return updateSheetConfig(cd, {
    sheetName,
    updateFn: (sheet) => {
      if (JSON.stringify(sheet.rowDeletions) === JSON.stringify(rowDeletions)) {
        return sheet;
      }

      return {
        ...sheet,
        rowDeletions: rowDeletions ?? [],
      };
    },
  });
};

const changesMapping = (
  mapping,
  { inputColumn, outputName, conversionType }
) => {
  if (
    mapping.inputColumn !== inputColumn ||
    mapping.outputName !== outputName ||
    mapping.conversions == null
  ) {
    return true;
  }
  return !(mapping.conversions ?? []).some(
    (conversion) => conversion.conversionType === conversionType
  );
};

const newMapping = (
  mapping = [],
  currentIndex,
  { inputColumn, outputName, conversions }
) => [
  ...mapping.slice(0, currentIndex === -1 ? mapping.length : currentIndex),
  ...(inputColumn == null
    ? []
    : [
        {
          inputColumn,
          outputName,
          conversions,
        },
      ]),
  ...mapping.slice(currentIndex === -1 ? mapping.length : currentIndex + 1),
];

export const updateColumnMapping = (
  cd,
  { inputColumn, outputName, conversionType }
) => {
  const safeMapping = cd.mapping ?? [];
  const currentIndex = safeMapping.findIndex(
    (mapping) => mapping.outputName === outputName
  );
  if (
    currentIndex >= 0 &&
    !changesMapping(safeMapping[currentIndex], {
      inputColumn,
      outputName,
      conversionType,
    })
  ) {
    return cd;
  }

  return {
    ...cd,
    mapping: newMapping(safeMapping, currentIndex, {
      inputColumn,
      outputName,
      conversions: [{ conversionType }],
    }),
  };
};

export const updateConversionMapping = (
  cd,
  { outputName, valueKey, mappedKey }
) => {
  const safeMapping = cd.mapping ?? [];
  const currentIndex = safeMapping.findIndex(
    (mapping) => mapping.outputName === outputName
  );
  const conversions = safeMapping[currentIndex]?.conversions ?? [];
  const allowedConversionTypes = [
    "CONVERT",
    "IN_DELETE_ROW",
    "NOT_IN_DELETE_ROW",
  ];
  const conversionIndex = conversions.findIndex((conversion) =>
    allowedConversionTypes.includes(conversion.conversionType)
  );
  if (conversionIndex === -1) {
    throw new Error(
      `Cannot update conversion mapping for types other than ${allowedConversionTypes.join(
        ", "
      )}`
    );
  }
  if (conversions[conversionIndex]?.conversions?.[valueKey] === mappedKey) {
    return cd;
  }
  return {
    ...cd,
    mapping: newMapping(safeMapping, currentIndex, {
      inputColumn: safeMapping[currentIndex].inputColumn,
      outputName,
      conversions: [
        ...conversions.slice(0, conversionIndex),
        {
          ...conversions[conversionIndex],
          conversions: {
            ...conversions[conversionIndex].conversions,
            [valueKey]: mappedKey,
          },
        },
        ...conversions.slice(conversionIndex + 1),
      ],
    }),
  };
};

export const updateDeleteInRowMapping = (
  cd,
  { outputName, valueKey, deleteInRow }
) => {
  const safeMapping = cd.mapping ?? [];
  const currentIndex = safeMapping.findIndex(
    (mapping) => mapping.outputName === outputName
  );
  const conversions = safeMapping[currentIndex]?.conversions ?? [];
  const conversionIndex = conversions.findIndex(
    (conversion) => conversion.conversionType === "IN_DELETE_ROW"
  );
  if (conversionIndex === -1) {
    throw new Error(
      `Cannot update delete in row mapping for ${outputName}: no IN_DELETE_ROW conversion found`
    );
  }
  const match = conversions[conversionIndex].match ?? [];
  if (deleteInRow && match.includes(valueKey)) {
    return cd;
  }
  if (!deleteInRow && !match.includes(valueKey)) {
    return cd;
  }
  return {
    ...cd,
    mapping: newMapping(safeMapping, currentIndex, {
      inputColumn: safeMapping[currentIndex].inputColumn,
      outputName,
      conversions: [
        ...conversions.slice(0, conversionIndex),
        {
          ...conversions[conversionIndex],
          match: deleteInRow
            ? [...match, valueKey]
            : match.filter((s) => s !== valueKey),
        },
        ...conversions.slice(conversionIndex + 1),
      ],
    }),
  };
};

export const updateConstantConversionValue = (cd, { outputName, value }) => {
  const safeMapping = cd.mapping ?? [];
  const currentIndex = safeMapping.findIndex(
    (mapping) => mapping.outputName === outputName
  );

  // If mapping doesn't exist
  if (currentIndex === -1) {
    // If value is null, do nothing
    if (value === null) {
      return cd;
    }
    // Create new mapping with CONSTANT conversion
    return {
      ...cd,
      mapping: [
        ...safeMapping,
        {
          inputColumn: 0, // Default input column
          outputName,
          conversions: [
            {
              conversionType: "CONSTANT",
              value,
            },
          ],
        },
      ],
    };
  }

  // If mapping exists, check conversion types
  const conversions = safeMapping[currentIndex]?.conversions ?? [];
  const hasNonConstantConversion = conversions.some(
    (conversion) => conversion.conversionType !== "CONSTANT"
  );

  // Throw error if mapping has non-CONSTANT conversion types
  if (hasNonConstantConversion) {
    throw new Error(
      `Cannot update constant conversion for column ${outputName} that has non-CONSTANT conversion types`
    );
  }

  const conversionIndex = conversions.findIndex(
    (conversion) => conversion.conversionType === "CONSTANT"
  );

  // If value is null, remove the entire mapping
  if (value === null) {
    return {
      ...cd,
      mapping: [
        ...safeMapping.slice(0, currentIndex),
        ...safeMapping.slice(currentIndex + 1),
      ],
    };
  }

  // If CONSTANT conversion exists, update it
  if (conversionIndex !== -1) {
    if (conversions[conversionIndex].value === value) {
      return cd; // No change needed
    }
    return {
      ...cd,
      mapping: newMapping(safeMapping, currentIndex, {
        inputColumn: safeMapping[currentIndex].inputColumn,
        outputName,
        conversions: [
          ...conversions.slice(0, conversionIndex),
          {
            ...conversions[conversionIndex],
            value,
          },
          ...conversions.slice(conversionIndex + 1),
        ],
      }),
    };
  }

  // If CONSTANT conversion doesn't exist, create it
  return {
    ...cd,
    mapping: newMapping(safeMapping, currentIndex, {
      inputColumn: safeMapping[currentIndex].inputColumn,
      outputName,
      conversions: [
        ...conversions,
        {
          conversionType: "CONSTANT",
          value,
        },
      ],
    }),
  };
};

export const updateDateConversionType = (cd, { column, type }) => {
  if ((cd.dateConversionTypes ?? {})[column] === type) {
    return cd;
  }
  return {
    ...cd,
    dateConversionTypes: {
      ...cd.dateConversionTypes,
      [column]: type,
    },
  };
};

export const setSubstitutions = (cd, { substitutions }) => {
  if (JSON.stringify(cd.substitutions) === JSON.stringify(substitutions)) {
    return cd;
  }
  return {
    ...cd,
    substitutions: substitutions ?? [],
  };
};

export const setRowDeletions = (cd, { rowDeletions }) => {
  if (JSON.stringify(cd.rowDeletions) === JSON.stringify(rowDeletions)) {
    return cd;
  }
  return {
    ...cd,
    rowDeletions: rowDeletions ?? [],
  };
};
